import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  InputSignal,
  OnDestroy,
  OnInit,
  inject,
  input,
} from '@angular/core';

import { IvyPinch } from './ivypinch';
import { Properties } from './interfaces';
import { defaultProperties } from './properties';

// Component is originally copied from https://github.com/olafvanv/ngx-pinch-zoom
// which is a fork of https://github.com/drozhzhin-n-e/ngx-pinch-zoom.
// It has been trimmed down to only the features actually used in this project:
//  - `backgroundColor` input (host background color)
//  - `limitZoom` input (max zoom limit)
//  - A single toggle-zoom control button (hidden on touch screens)

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-pinch-zoom',
  templateUrl: './pinch-zoom.component.html',
  styleUrls: ['./pinch-zoom.component.scss'],
  host: {
    '[style.overflow]': '"hidden"',
    '[style.background-color]': 'backgroundColor()',
  },
})
export class PinchZoomComponent implements OnInit, OnDestroy {
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  public readonly backgroundColor: InputSignal<string> =
    input<string>('rgba(0,0,0,0.85)');
  public readonly limitZoom: InputSignal<number | 'original image size'> =
    input<number | 'original image size'>('original image size');

  private pinchZoom: IvyPinch | undefined;

  public ngOnInit(): void {
    const properties: Properties = {
      ...defaultProperties,
      limitZoom: this.limitZoom(),
      element: this.elementRef.nativeElement.querySelector(
        '.pinch-zoom-content',
      ) as unknown as string,
    };
    this.pinchZoom = new IvyPinch(properties);

    /* Calls the method until the image size is available */
    this.pinchZoom.detectLimitZoom();
  }

  public ngOnDestroy(): void {
    this.pinchZoom?.destroy();
  }

  protected get isDragging(): boolean | undefined {
    return this.pinchZoom?.isDragging();
  }

  protected get isZoomedIn(): boolean {
    return (this.pinchZoom?.scale ?? 1) > 1;
  }

  protected isControl(): boolean {
    return !this.isTouchScreen();
  }

  protected toggleZoom(): void {
    this.pinchZoom?.toggleZoom();
  }

  private isTouchScreen(): boolean {
    if ('ontouchstart' in window) {
      return true;
    }
    const prefixes: string[] = ' -webkit- -moz- -o- -ms- '.split(' ');
    const query: string = [
      '(',
      prefixes.join('touch-enabled),('),
      'heartz',
      ')',
    ].join('');
    return window.matchMedia(query).matches;
  }
}
